import React from 'react';
import { ProjectColors } from '../../General/projectColors';
import './ColorCodedCell.css';

// Calculate color intensity based on value within range
const calculateColorIntensity = (value, range) => {
  if (range.max === range.min || range.max === 0) return 0;
  return Math.min(Math.max((value - range.min) / (range.max - range.min), 0), 1);
};

// Generate background color based on intensity with enhanced gradients
const getBackgroundColor = (intensity, isMaxValue, value) => {
  if (isMaxValue && value > 0) {
    return `linear-gradient(135deg, ${ProjectColors.Purple} 0%, ${ProjectColors.DarkBlue} 100%)`;
  }
  
  if (intensity === 0 || value === 0) {
    return 'transparent';
  }
  
  // Enhanced blue gradient heatmap with smoother transitions and modern colors
  if (intensity < 0.15) {
    const alpha = 0.06 + (intensity * 0.12);
    return `linear-gradient(135deg, rgba(59, 130, 246, ${alpha}) 0%, rgba(147, 197, 253, ${alpha * 0.8}) 100%)`;
  } else if (intensity < 0.35) {
    const alpha = 0.18 + ((intensity - 0.15) * 0.25);
    return `linear-gradient(135deg, rgba(59, 130, 246, ${alpha}) 0%, rgba(99, 179, 237, ${alpha * 0.9}) 100%)`;
  } else if (intensity < 0.55) {
    const alpha = 0.43 + ((intensity - 0.35) * 0.22);
    return `linear-gradient(135deg, rgba(37, 99, 235, ${alpha}) 0%, rgba(59, 130, 246, ${alpha * 0.95}) 100%)`;
  } else if (intensity < 0.75) {
    const alpha = 0.65 + ((intensity - 0.55) * 0.18);
    return `linear-gradient(135deg, rgba(29, 78, 216, ${alpha}) 0%, rgba(37, 99, 235, ${alpha}) 100%)`;
  } else {
    const alpha = 0.83 + ((intensity - 0.75) * 0.15);
    return `linear-gradient(135deg, rgba(30, 64, 175, ${alpha}) 0%, rgba(29, 78, 216, ${alpha * 0.98}) 100%)`;
  }
};

// text color logic
const getTextColor = (intensity, isMaxValue) => {
  if (isMaxValue) return 'white';
  return intensity > 0.5 ? 'white' : '#0f172a';
};

// font weight logic
const getFontWeight = (intensity, isMaxValue) => {
  if (isMaxValue) return '700';
  return intensity > 0.4 ? '600' : '500';
};

// Get box shadow for depth
const getBoxShadow = (intensity, isMaxValue, value) => {
  if (isMaxValue && value > 0) {
    return '0 4px 12px rgba(147, 51, 234, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
  }
  if (intensity > 0.6) {
    return 'inset 0 1px 0 rgba(255, 255, 255, 0.1)';
  }
  return 'none';
};

export default function ColorCodedCell({ value, displayValue, range, isMaxValue, isLastColumn = false }) {

  const intensity = calculateColorIntensity(value, range);
  
  const backgroundColor = getBackgroundColor(intensity, isMaxValue, value);
  const textColor = getTextColor(intensity, isMaxValue);
  const fontWeight = getFontWeight(intensity, isMaxValue);
  const boxShadow = getBoxShadow(intensity, isMaxValue, value);

  // Event handlers
  const handleMouseEnter = (e) => {
    if (isMaxValue && value > 0) {
      e.currentTarget.style.boxShadow = '0 6px 16px rgba(147, 51, 234, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
    }
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.boxShadow = boxShadow;
  };

  return (
    <td 
      className={`color-coded-cell ${isLastColumn ? 'last-column' : ''}`}
      style={{
        background: backgroundColor,
        color: textColor,
        fontWeight: fontWeight,
        boxShadow: boxShadow,
        fontSize: '14px',
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="cell-content">
        {displayValue}
      </div>
      {isMaxValue && value > 0 && (
        <div 
          className="max-value-badge"
          title="Highest value in this category"
        >
          ★
        </div>
      )}
    </td>
  );
}
